import React, { useMemo, useRef, useEffect } from "react";
import { useMeeting } from "@videosdk.live/react-sdk";
import { MemoizedParticipantGrid } from "../../components/ParticipantGrid";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useBreakoutRooms } from "../../context/BreakoutRoomsContext";

function ParticipantsViewer({ isPresenting, isFlexCol, layout }) {
  const {
    participants,
    pinnedParticipants,
    activeSpeakerId,
    localParticipant,
    localScreenShareOn,
    presenterId,
  } = useMeeting();
  const { hideSelfView, showNonVideoParticipants, localPinnedParticipant } = useSiteContext();
  const { currentFocusParticipantId } = useMeetingAppContext();
  const breakoutRoomsContext = useBreakoutRooms();
  const { isInSameRoom, currentRoom, participantRooms } = breakoutRoomsContext || {};
  const previousActiveSpeakerIdRef = useRef(null);
  const currentActiveSpeakerIdRef = useRef(null);

  // Track the last active speaker ID (previous speaker before current)
  useEffect(() => {
    if (activeSpeakerId && activeSpeakerId !== currentActiveSpeakerIdRef.current) {
      // Store the previous active speaker before updating
      if (currentActiveSpeakerIdRef.current) {
        previousActiveSpeakerIdRef.current = currentActiveSpeakerIdRef.current;
      }
      currentActiveSpeakerIdRef.current = activeSpeakerId;
    } else if (!activeSpeakerId) {
      // If activeSpeakerId becomes null, clear both refs
      currentActiveSpeakerIdRef.current = null;
      previousActiveSpeakerIdRef.current = null;
    }
  }, [activeSpeakerId]);

  const participantIds = useMemo(() => {
    console.log("pinParticipants", pinnedParticipants);
    const pinnedParticipantId = [...pinnedParticipants.keys()];
    const regularParticipantIds = [...participants.keys()].filter(
      (participantId) => {
        return (
          ![...pinnedParticipants.keys()].includes(participantId) &&
          localParticipant.id !== participantId
        );
      }
    );

    // Pass all participants - ParticipantGrid will handle pagination
    let ids = [
      localParticipant.id,
      ...localPinnedParticipant,
      ...regularParticipantIds,
    ];

    // Don't add active speaker if we're in speaker-top or speaker-side layout
    // (it will be shown in the main speaker view, not in sidebar)
    if (activeSpeakerId && layout !== "speaker-top" && layout !== "speaker-side") {
      if (!ids.includes(activeSpeakerId)) {
        ids[ids.length - 1] = activeSpeakerId;
      }

    }

    if (hideSelfView) {
      ids = ids.filter((id) => id !== localParticipant.id);
    } else if (layout === "speaker-top" || layout === "speaker-side") {
      // Only filter out self if self is the active speaker (shown in main view)
      // If self is NOT the active speaker, show self in sidebar
      if (activeSpeakerId === localParticipant.id) {
        ids = ids.filter((id) => id !== localParticipant.id);
      }
    }

    if (layout === "speaker-top" || layout === "speaker-side") {
      // Filter out the latest (current) active speaker from sidebar
      if (activeSpeakerId) {
        ids = ids.filter((id) => id !== activeSpeakerId);
      }
      // Filter out the current focus participant from SpeakerParticipantView (should not be in sidebar)
      if (currentFocusParticipantId) {
        ids = ids.filter((id) => id !== currentFocusParticipantId);
      }
    }

    if (showNonVideoParticipants) {
      ids = ids.filter((participantId) => {
        const participant = participants.get(participantId);
        console.log("participant", participant);
        return participant?.webcamOn == true;
      });
    }

    if (pinnedParticipantId.length > 0) {
      ids = [...pinnedParticipantId]
    }

    // Filter by breakout room - hide participants assigned to rooms from main meeting
    // Only show participants in the same room when in a breakout room
    // Host view should be same like other participant screen - no special treatment
    if (breakoutRoomsContext) {
      ids = ids.filter((participantId) => {
        // Use isInSameRoom for all participants including host/self
        // This ensures host is treated the same as other participants
        return isInSameRoom?.(participantId) ?? true;
      });
    }

    // remove duplicate ids from ids array
    ids = [...new Set(ids)];

    return ids;
  }, [
    participants,
    activeSpeakerId,
    pinnedParticipants,
    presenterId,
    hideSelfView,
    localScreenShareOn,
    showNonVideoParticipants,
    layout,
    localPinnedParticipant,
    currentFocusParticipantId,
    currentRoom,
    isInSameRoom,
    participantRooms,
    localParticipant.id,
  ]);

  return (
    <MemoizedParticipantGrid
      participantIds={participantIds}
      isPresenting={isPresenting}
      isFlexCol={isFlexCol}
      layout={layout}
    />
  );
}

const MemorizedParticipantView = React.memo(
  ParticipantsViewer,
  (prevProps, nextProps) => {
    return prevProps.isPresenting === nextProps.isPresenting;
  }
);

export default MemorizedParticipantView;

